import { z } from 'zod';
import { SPECIAL_ABILITIES } from '../game/abilities.js';
import {
  ACCESSORIES,
  BACKPACKS,
  BODY_MODELS,
  EXPRESSIONS,
  GLASSES,
  HAIR_COLORS,
  HAIR_STYLES,
  HATS,
  PALETTE,
  SHIRTS,
  SKIN_TONES,
  type CosmeticOption,
} from '../game/cosmetics.js';
import { EMOTES, QUICK_MESSAGES } from '../game/progression.js';
import { CHAT_LIMITS, NAME_LIMITS, ROOM_LIMITS } from '../constants.js';
import { NAME_RE } from '../text/filter.js';

const ids = (list: CosmeticOption[]) => z.enum(list.map((o) => o.id) as [string, ...string[]]);
const index = (n: number) => z.number().int().min(0).max(n - 1);
const finite = z.number().finite();

export const characterConfigSchema = z
  .object({
    body: ids(BODY_MODELS),
    skin: index(SKIN_TONES.length),
    hair: ids(HAIR_STYLES),
    hairColor: index(HAIR_COLORS.length),
    hat: ids(HATS),
    glasses: ids(GLASSES),
    shirt: ids(SHIRTS),
    shirtColor: index(PALETTE.length),
    pantsColor: index(PALETTE.length),
    shoesColor: index(PALETTE.length),
    backpack: ids(BACKPACKS),
    accessory: ids(ACCESSORIES),
    expression: ids(EXPRESSIONS),
    headScale: z.number().min(0.9).max(1.3),
    useFace: z.boolean(),
  })
  .strict();

export const playerNameSchema = z
  .string()
  .transform((s) => s.normalize('NFKC').replace(/\s+/g, ' ').trim())
  .pipe(z.string().min(NAME_LIMITS.MIN).max(NAME_LIMITS.MAX).regex(NAME_RE, 'Solo letras, números, espacios, - y _'));

export const usernameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.string().min(3).max(24).regex(/^[a-z0-9_.-]+$/, 'Usuario: letras, números, _ . -'));

export const passwordSchema = z.string().min(8).max(128);

export const guestSchema = z.object({ name: playerNameSchema }).strict();
export const registerSchema = z.object({ username: usernameSchema, password: passwordSchema }).strict();
export const loginSchema = registerSchema;
export const renameSchema = z.object({ name: playerNameSchema }).strict();

export const roomCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .pipe(z.string().length(ROOM_LIMITS.CODE_LENGTH).regex(new RegExp(`^[${ROOM_LIMITS.CODE_ALPHABET}]+$`)));

const empty = z.object({}).strict();

export const moveInputSchema = z
  .object({
    seq: z.number().int().min(0).max(2 ** 31),
    mx: finite.min(-1).max(1),
    mz: finite.min(-1).max(1),
    yaw: finite.min(-10).max(10),
    jump: z.boolean(),
    sprint: z.boolean(),
  })
  .strict();

export const clientSchemas = {
  'room:create': z
    .object({
      visibility: z.enum(['public', 'private']),
      maxPlayers: z.number().int().min(ROOM_LIMITS.MIN_CONFIGURABLE_PLAYERS).max(ROOM_LIMITS.ABSOLUTE_MAX_PLAYERS).optional(),
      name: z.string().max(32).optional(),
    })
    .strict(),
  'room:join': z.object({ code: roomCodeSchema }).strict(),
  'room:quickplay': empty,
  'room:list': empty,
  'room:leave': empty,
  'room:start': empty,
  'room:toLobby': empty,
  'room:config': z
    .object({
      visibility: z.enum(['public', 'private']).optional(),
      maxPlayers: z.number().int().min(ROOM_LIMITS.MIN_CONFIGURABLE_PLAYERS).max(ROOM_LIMITS.ABSOLUTE_MAX_PLAYERS).optional(),
      missionId: z.string().max(40).optional(),
      minigame: z.boolean().optional(),
      pranks: z.boolean().optional(),
    })
    .strict(),
  'room:kick': z.object({ playerId: z.string().uuid() }).strict(),
  input: z.object({ inputs: z.array(moveInputSchema).min(1).max(12) }).strict(),
  'act:interact': empty,
  'act:ability': z.object({ ability: z.enum(['push', ...SPECIAL_ABILITIES] as [string, ...string[]]), aimYaw: finite.min(-10).max(10) }).strict(),
  'act:emote': z.object({ emote: z.enum(EMOTES.map((e) => e.id) as [string, ...string[]]) }).strict(),
  'loadout:set': z.object({ special: z.enum(SPECIAL_ABILITIES as [string, ...string[]]) }).strict(),
  'chat:send': z
    .object({
      text: z.string().max(CHAT_LIMITS.MAX_LENGTH * 2).optional(),
      quick: z.number().int().min(0).max(QUICK_MESSAGES.length - 1).optional(),
    })
    .strict()
    .refine((p) => (p.text === undefined) !== (p.quick === undefined), 'Envía texto o un mensaje rápido'),
  'player:block': z.object({ playerId: z.string().uuid(), blocked: z.boolean() }).strict(),
  'player:report': z
    .object({
      playerId: z.string().uuid(),
      reason: z.enum(['harassment', 'cheating', 'offensive-name', 'offensive-avatar', 'spam', 'other']),
      details: z.string().max(300).optional(),
    })
    .strict(),
  'character:refresh': empty,
  'time:ping': z.object({ c: finite }).strict(),
} as const;

export type ClientEventName = keyof typeof clientSchemas;
